import { AxiosResponse } from "axios";
import { useDispatch, useSelector } from "react-redux";
import { CLIENT_GET_ROOM, CLIENT_GET_ROOM_MESSAGE } from "../constants/methods";
import { STICKER_GROUP } from "../constants/webservices";
import { StickerGroupType } from "../containers/popup/popup-sticker-group";
import { RegisteredUser, Room, RoomMessage } from "../proto/Global_pb";
import store, { ReducersType } from "../store";
import { addStickerGroup, setMessengerPlayerAudio } from "../store/messenger/actions";
import { SignalingStateType } from "../store/signaling/types";
import { protoHandler } from "./proto";
import { restHandler } from "./rest";
import { goToLastMessage, goToMessenger, sortForwardList } from "./services";
import { areEqual, memorize } from "./utils";
import { getStorage, storagePrefix } from "./storage";
import { STORAGE_USER_ID } from "../constants/localstorage";
import { cloudUserData, FileStatus } from "../constants/globals";
import { debounce, filter } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { UserInfoResponse } from "../proto/UserInfo_pb";
import AudioClass from "./audio-player";
import { useMediaWrapper } from "../containers/media-wrapper";
import { getUser as getUserRedux } from "../store/users/actions";

export const getUser = memorize((userId: RegisteredUser.AsObject["id"]) => {
  store.dispatch(getUserRedux({ userId }));
  return Promise.resolve();
});
export const getRoom = memorize((roomId: Room.AsObject["id"]) => protoHandler(CLIENT_GET_ROOM, { roomId }));
export const getMessage = memorize((messageId: RoomMessage.AsObject["messageId"], roomId: Room.AsObject["id"]) =>
  protoHandler(CLIENT_GET_ROOM_MESSAGE, {
    messageId,
    roomId,
  })
);

type getUserOption = {
  checkCloud?: boolean;
};

export type forwardArray = {
  roomId?: Room.AsObject["id"] | "";
  userId: RegisteredUser.AsObject["id"] | "";
  username?: RegisteredUser.AsObject["username"];
  avatar?: RegisteredUser.AsObject["avatar"];
  key: RegisteredUser.AsObject["id"] | Room.AsObject["id"];
  displayName: RegisteredUser.AsObject["displayName"] | Room.AsObject["title"];
  initials?: RegisteredUser.AsObject["initials"] | Room.AsObject["initials"];
  type?: Room.AsObject["type"];
  color?: RegisteredUser.AsObject["color"] | Room.AsObject["color"];
  updateTime?: RoomMessage.AsObject["updateTime"];
  pinId?: Room.AsObject["pinId"];
};

export type multiAccountType = { data: Array<RegisteredUser.AsObject | undefined>; loading: boolean };
export type useAsyncType = { status: "idle" | "pending" | "resolved" | "rejected"; data?: any; error?: string };
export type useAsyncReturnType<D> = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isIdle: boolean;
  setData: any;
  setError: any;
  error: string;
  status: useAsyncType["status"];
  data: D;
  run: (promise: Promise<any>) => {};
  reset: () => void;
};

export type useAsyncAllType = { status: "idle" | "pending" | "resolved" | "rejected"; data?: any; errors?: any[] };
export type useAsyncAllReturnType = {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
  isIdle: boolean;

  setData: any;
  setErrors: any;
  errors: useAsyncAllType["errors"];
  status: useAsyncAllType["status"];
  data: any[];
  runAll: (promises: Promise<any>[], allSettled?: boolean) => void;
  reset: () => void;
};

export const useGetUser = (userId?: RegisteredUser.AsObject["id"], options?: getUserOption) => {
  const user = useSelector<ReducersType, RegisteredUser.AsObject | undefined>((state) => state.users.users[`${userId}`]);

  if (user === undefined && userId !== undefined) {
    store.dispatch(getUserRedux({ userId }));
  }
  return user;
};

export const useGetRoom = (roomId?: Room.AsObject["id"]) => {
  const room = useSelector<ReducersType, Room.AsObject | undefined>((state) => state.rooms.rooms[`${roomId}`], areEqual);
  if (room === undefined && roomId !== undefined) {
    getRoom(roomId).catch(goToMessenger);
  }
  return room;
};

export const useCurrentRoom = () => {
  const currentRoomId = useSelector<ReducersType, Room.AsObject["id"] | undefined>((state) => state.rooms.currentRoom, areEqual);
  const room = useSelector<ReducersType, Room.AsObject | undefined>((state) => state.rooms.rooms[`${currentRoomId}`], areEqual);
  if (room === undefined && currentRoomId !== undefined) {
    getRoom(currentRoomId).catch(goToMessenger);
  }
  return room;
};

export const useGetMessage = (messageId?: RoomMessage.AsObject["messageId"], roomId?: Room.AsObject["id"]) => {
  const message = useSelector<ReducersType, RoomMessage.AsObject | undefined>((state) => state.messages.messages[`${messageId}`], areEqual);
  if (message === undefined && messageId !== undefined && roomId !== undefined) {
    getMessage(messageId, roomId).then();
  }
  return message;
};

export const useGetStickerGroup = (groupId: StickerGroupType["_id"]) => {
  const dispatch = useDispatch();
  const stickerGroup = useSelector<ReducersType, StickerGroupType>((state) => state.messenger.stickerGroups[groupId], areEqual);
  if (stickerGroup === undefined) {
    restHandler({ url: STICKER_GROUP, urlParams: { groupId: groupId } }, true).then((res: AxiosResponse<StickerGroupType>) => {
      dispatch(addStickerGroup([res.data]));
    });
  }
  return stickerGroup;
};

export const useGetSignalingStatus = () => {
  return useSelector<ReducersType, SignalingStateType["status"]>((state) => state.signaling.status, areEqual);
};

export const useGetForwardList = (): forwardArray[] => {
  const array: forwardArray[] = [];

  useSelector<ReducersType, any>(
    (state) =>
      Object.values(sortForwardList(state.rooms.rooms))
        .filter((i) => (!i.readOnly && i.type === Room.Type.CHAT) || i.permission?.postMessage || (i.type === Room.Type.GROUP && i.permission?.postMessage))
        .forEach((item) =>
          array.push({
            key: item.id,
            roomId: item.id,
            userId: "",
            username: item.channelRoomExtra?.publicExtra?.username || item.groupRoomExtra?.publicExtra?.username || item.chatRoomExtra?.peer?.username,
            avatar: item.channelRoomExtra?.avatar || item.groupRoomExtra?.avatar || item.chatRoomExtra?.peer?.avatar,
            displayName: item.title,
            initials: item.initials || item.chatRoomExtra?.peer?.initials,
            type: item.type,
            color: item.color || item?.chatRoomExtra?.peer?.color,
            updateTime: item.lastMessage?.updateTime || item?.lastMessage?.createTime,
            pinId: item?.pinId,
          })
        ),
    areEqual
  );

  useSelector<ReducersType, any>(
    (state) =>
      Object.values(state.users.users).forEach((item) => {
        const duplicated = array.some((i) => i.username === item.username);
        if (!duplicated) {
          array.push({
            key: item.id,
            roomId: "",
            userId: item.id,
            username: item.username,
            avatar: item.avatar,
            displayName: item.displayName,
            initials: item.initials,
            type: undefined,
            color: item.color,
          });
        }
      }),
    areEqual
  );

  return filter(array, "displayName");
};

export const useGetAccounts = (): multiAccountType => {
  const [data, setData] = useState<Array<RegisteredUser.AsObject>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  useEffect(() => {
    setLoading(true);
    const countAccount = 3;
    const promises: Array<Promise<UserInfoResponse.AsObject>> = [];
    for (let i = 1; i <= +countAccount; i++) {
      const prefix = i === 1 ? "" : `${i}__`;
      const userId = localStorage.getItem(storagePrefix + prefix + STORAGE_USER_ID);
      // if (userId) promises.push(getUser(userId));
    }
    Promise.all(promises).then((userInfoResponses) => {
      setData(userInfoResponses.map((user) => user.user!));
      setLoading(false);
    });
  }, []);

  return { data, loading };
};

export const hasAccount = () => {
  const countAccount = 3;
  for (let i = 1; i <= +countAccount; i++) {
    const prefix = i === 1 ? "" : `${i}__`;
    const storageUserId = localStorage.getItem(storagePrefix + prefix + STORAGE_USER_ID);
    if (storageUserId) {
      return i;
    }
  }
};

function useSafeDispatch<T>(dispatch: T | any): (args: T) => void {
  const mounted = useRef(false);
  // @ts-ignore
  useLayoutEffect(() => {
    mounted.current = true;
    return () => (mounted.current = false);
  }, []);
  return useCallback((...args) => (mounted.current ? dispatch(...args) : void 0), [dispatch]);
}

export const useAsync = <D>(initialState?: useAsyncType): useAsyncReturnType<D> => {
  const initialRef = useRef<useAsyncType>({
    status: "idle",
    ...initialState,
  });

  const [{ status, data, error }, setState] = useReducer((prevState: useAsyncType, newState: useAsyncType) => ({ ...prevState, ...newState }), initialRef.current);

  const safeSetState = useSafeDispatch<useAsyncType>(setState);

  const setData = useCallback((data) => safeSetState({ data, status: "resolved" }), [safeSetState]);

  const setError = useCallback((error) => safeSetState({ error, status: "rejected" }), [safeSetState]);

  const reset = useCallback(() => safeSetState(initialRef.current), [safeSetState]);

  const run = useCallback(
    (promise: Promise<any>) => {
      if (!promise || !promise.then) {
        throw new Error(`The argument passed to useAsync().run must be a promise. Maybe a function that's passed isn't returning anything?`);
      }
      safeSetState({ status: "pending" });
      return promise.then(
        (data: D) => {
          setData(data);
          return data;
        },
        (error) => {
          setError(error);
          return Promise.reject(error);
        }
      );
    },
    [safeSetState, setData, setError]
  );

  return {
    isLoading: status === "pending",
    isError: status === "rejected",
    isSuccess: status === "resolved",
    isIdle: status === "idle",

    setData,
    setError,
    error: error || "",
    status,
    data,
    run,
    reset,
  };
};

export const useAsyncAll = (initialState?: useAsyncAllType): useAsyncAllReturnType => {
  const initialRef = useRef<useAsyncType>({
    status: "idle",
    ...initialState,
  });

  const [{ status, data, errors }, setState] = useReducer((prevState: useAsyncAllType, newState: useAsyncAllType) => ({ ...prevState, ...newState }), initialRef.current);

  const safeSetState = useSafeDispatch<useAsyncAllType>(setState);

  const setData = useCallback((data) => safeSetState({ data, status: "resolved" }), [safeSetState]);

  const setErrors = useCallback((errors) => safeSetState({ errors, status: "rejected" }), [safeSetState]);

  const reset = useCallback(() => safeSetState(initialRef.current), [safeSetState]);

  const runAll = useCallback(
    (promises: Promise<any>[], allSettled = false) => {
      if (!Array.isArray(promises)) {
        throw new Error(`The argument passed to useAsync().runAll must be an array of promises.`);
      }

      safeSetState({ status: "pending" });

      if (allSettled) {
        return Promise.allSettled(promises).then((data: any[]) => {
          setData(data);
          return data;
        });
      } else {
        return Promise.all(promises)
          .then((data: any[]) => {
            setData(data);
            return data;
          })
          .catch((errors: any) => {
            setErrors(errors);
            return errors;
          });
      }
    },
    [safeSetState, setData, setErrors]
  );

  return {
    isLoading: status === "pending",
    isError: status === "rejected",
    isSuccess: status === "resolved",
    isIdle: status === "idle",

    setData,
    setErrors,
    errors: errors || [],
    status,
    data: data || [],
    runAll,
    reset,
  };
};

export const useVerticalScroll = (): { visible: boolean; ref: any; scrollToBottom: () => void } => {
  const elRef = useRef();
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const el: any = elRef.current;
    if (el) {
      const onWheel = (event: any) => {
        if (event.target.scrollTop <= -30) {
          setVisible(true);
        } else {
          setVisible(false);
        }
      };

      el.addEventListener("scroll", onWheel);
      return () => el.removeEventListener("scroll", onWheel);
    }
  }, []);

  const scrollToBottom = () => {
    const el: any = elRef.current;
    // console.log(el);
    /* @ts-ignore */
    // el.scrollTop = 0;

    // el.scrollTo({
    //   behavior: "smooth",
    //   top: 0,
    // });
    goToLastMessage();
  };

  return { ref: elRef, visible, scrollToBottom };
};

export const useAudio = () => {
  const { fileStatus, fileUrl, file, download, messageId } = useMediaWrapper();
  const playingMessageId = useSelector<ReducersType, RoomMessage.AsObject["messageId"] | undefined>((state) => state.messenger.playerAudioMessageId.messageId, areEqual);
  const isPlayingAudioId = playingMessageId === messageId;
  let audioMessageId = isPlayingAudioId && playingMessageId !== undefined ? playingMessageId : messageId;

  useEffect(() => {
    if (isPlayingAudioId) AudioClass.refreshAudioMessages("play");
    if (AudioClass.messageId !== playingMessageId) changeAudio().then();
  }, [fileUrl]);

  const downloadFile = () => {
    AudioClass.pause();
    download().then(() => {
      changeAudio().then(() => {
        store.dispatch(setMessengerPlayerAudio({ messageId: audioMessageId, status: FileStatus.DOWNLOADED }));
      });
    });
  };
  const togglePlayer = () => {
    store.dispatch(setMessengerPlayerAudio({ messageId: audioMessageId, status: fileUrl ? FileStatus.DOWNLOADED : FileStatus.Downloading }));
    if (!fileUrl || fileStatus !== FileStatus.DOWNLOADED) return downloadFile();
    changeAudio().then();
  };
  const changeAudio = async () => {
    if (!fileUrl) return;
    await AudioClass.changeAudio({
      src: fileUrl,
      messageId: audioMessageId,
      duration: file?.duration,
    });
  };

  return { togglePlayer: togglePlayer, duration: file?.duration };
};

export const useTimer = (minute?: number, second?: number) => {
  const [minutes, setMinutes] = useState(minute || 0);
  const [seconds, setSeconds] = useState(second || 0);
  useEffect(() => {
    let myInterval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }
      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(myInterval);
        } else {
          setMinutes(minutes - 1);
          setSeconds(59);
        }
      }
    }, 1000);
    return () => {
      clearInterval(myInterval);
    };
  });
  let countDown = `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  return { minutes, seconds, countDown };
};

export const useDebounce = (fn: (...args: any[]) => any, timeout: number = 500) => {
  const debouncedFunc = debounce(fn, timeout);

  useEffect(() => {
    return debouncedFunc.cancel();
  }, []);

  return debouncedFunc;
};
